/**
 * @fileoverview Service for managing document issuance
 */
import { contractService } from '@/services/web3/contractService';
import { ipfsService } from '@/services/ipfs/ipfsService';
/**
 * Document service for issuance operations
 */
class DocumentService {
    storageKey = 'certiChain_documents';
    /**
     * Issue a document using advanced mode (with template)
     */
    async issueDocument(issuance, issuerId, issuerName) {
        try {
            // Prepare metadata hash
            const metadataString = JSON.stringify({
                ...issuance.metadata,
                fieldValues: issuance.fieldValues,
                templateId: issuance.templateId,
            });
            const metadataHash = await this.hashString(metadataString);
            // Upload PDF to IPFS if provided
            let ipfsHash = '';
            let pdfHash = '';
            if (issuance.pdfFile) {
                pdfHash = await this.hashFile(issuance.pdfFile);
                // Upload to IPFS
                try {
                    ipfsHash = await ipfsService.uploadFile(issuance.pdfFile);
                }
                catch (error) {
                    console.warn('IPFS upload failed, using hash as fallback:', error);
                    ipfsHash = pdfHash; // Fallback to hash if IPFS fails
                }
            }
            // Upload metadata to IPFS
            let metadataIpfsHash = '';
            try {
                metadataIpfsHash = await ipfsService.uploadMetadata({
                    ...issuance.metadata,
                    fieldValues: issuance.fieldValues,
                    templateId: issuance.templateId,
                });
            }
            catch (error) {
                console.warn('Metadata IPFS upload failed, using hash as fallback:', error);
                metadataIpfsHash = metadataHash; // Fallback
            }
            // Register on blockchain
            const blockchainHash = await this.registerOnBlockchain(issuance.recipientName || issuance.recipientEmail, issuance.recipientEmail, metadataHash);
            // Create document record
            const document = {
                id: this.generateId(),
                templateId: issuance.templateId,
                issuerId,
                issuerName,
                recipientEmail: issuance.recipientEmail,
                recipientName: issuance.recipientName,
                fieldValues: issuance.fieldValues,
                metadata: issuance.metadata,
                pdfHash: issuance.pdfFile ? pdfHash : undefined,
                blockchainHash,
                ipfsHash: ipfsHash || metadataIpfsHash || blockchainHash,
                status: 'issued',
                issuedAt: new Date(),
                transactionHash: blockchainHash, // Placeholder
            };
            // Save to localStorage
            this.saveDocument(document);
            // TODO: Send email to recipient
            return document;
        }
        catch (error) {
            console.error('Error issuing document:', error);
            throw error;
        }
    }
    /**
     * Issue a document using basic mode (PDF only)
     */
    async issueBasicDocument(issuance, issuerId, issuerName) {
        try {
            // Hash PDF file
            const pdfHash = await this.hashFile(issuance.pdfFile);
            // Upload to IPFS
            let ipfsHash = '';
            try {
                ipfsHash = await ipfsService.uploadFile(issuance.pdfFile);
            }
            catch (error) {
                console.warn('IPFS upload failed, using hash as fallback:', error);
                ipfsHash = pdfHash; // Fallback to hash if IPFS fails
            }
            // Register on blockchain
            const blockchainHash = await this.registerOnBlockchain(issuance.recipientName || issuance.recipientEmail, issuance.recipientEmail, pdfHash);
            // Create document record
            const document = {
                id: this.generateId(),
                templateId: issuance.templateId,
                issuerId,
                issuerName,
                recipientEmail: issuance.recipientEmail,
                recipientName: issuance.recipientName,
                fieldValues: {},
                metadata: issuance.metadata || {},
                pdfHash,
                blockchainHash,
                ipfsHash,
                status: 'issued',
                issuedAt: new Date(),
                transactionHash: blockchainHash, // Placeholder
            };
            // Save to localStorage
            this.saveDocument(document);
            // TODO: Send email to recipient
            return document;
        }
        catch (error) {
            console.error('Error issuing basic document:', error);
            throw error;
        }
    }
    /**
     * Get all issued documents
     */
    getDocuments(issuerId) {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (!stored)
                return [];
            const documents = JSON.parse(stored);
            if (issuerId) {
                return documents.filter(d => d.issuerId === issuerId);
            }
            return documents;
        }
        catch (error) {
            console.error('Error loading documents:', error);
            return [];
        }
    }
    /**
     * Get a document by ID
     */
    getDocumentById(documentId) {
        const documents = this.getDocuments();
        return documents.find(d => d.id === documentId) || null;
    }
    /**
     * Get documents by recipient email
     */
    getDocumentsByRecipient(recipientEmail) {
        try {
            const documents = this.getDocuments();
            return documents.filter(d => d.recipientEmail.toLowerCase() === recipientEmail.toLowerCase());
        }
        catch (error) {
            console.error('Error loading documents by recipient:', error);
            return [];
        }
    }
    /**
     * Verify a document
     */
    async verifyDocument(documentId) {
        const document = this.getDocumentById(documentId);
        if (!document) {
            throw new Error('Document not found');
        }
        // Verify on blockchain
        try {
            // TODO: Implement blockchain verification
            return true;
        }
        catch (error) {
            console.error('Error verifying document:', error);
            return false;
        }
    }
    /**
     * Register document on blockchain
     */
    async registerOnBlockchain(recipientName, recipientEmail, hash) {
        try {
            // Use contract service to register
            const transactionHash = await contractService.issueCertificate({
                beneficiaryName: recipientName,
                idOrLicense: recipientEmail,
                certificateType: 'Document',
                issueDate: new Date().toISOString(),
                metadataHash: hash,
            });
            return transactionHash || hash;
        }
        catch (error) {
            console.error('Error registering on blockchain:', error);
            // Return hash as fallback
            return hash;
        }
    }
    /**
     * Hash a string
     */
    async hashString(str) {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    /**
     * Hash a file
     */
    async hashFile(file) {
        const arrayBuffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    /**
     * Save document to localStorage
     */
    saveDocument(document) {
        try {
            const documents = this.getDocuments();
            documents.push(document);
            localStorage.setItem(this.storageKey, JSON.stringify(documents));
        }
        catch (error) {
            console.error('Error saving document:', error);
            throw new Error('Failed to save document');
        }
    }
    /**
     * Generate a unique ID
     */
    generateId() {
        return `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Initialize mock documents for test user
     */
    initializeMockDocuments() {
        const testRecipientEmail = 'testcertificado@test.com';
        // Check if mock documents already exist
        const existingDocs = this.getDocumentsByRecipient(testRecipientEmail);
        if (existingDocs.length > 0) {
            console.log('Mock documents already exist for', testRecipientEmail);
            return;
        }
        console.log('Creating mock documents for', testRecipientEmail);
        // Mock document 1: Certificado de Piloto
        const doc1 = {
            id: 'mock_doc_1',
            templateId: 'mock_template_pilot',
            templateName: 'Certificado de Piloto Comercial',
            issuerId: 'testadmin@test.com',
            issuerName: 'Academia de Aviación Test',
            recipientEmail: testRecipientEmail,
            recipientName: 'Juan Pérez',
            fieldValues: {
                nombre: 'Juan Pérez',
                licencia: 'PPL-12345',
                vencimiento: '2025-12-31',
                horasVuelo: '250',
                tipoAeronave: 'Cessna 172',
            },
            metadata: {
                tipo: 'certificado_piloto',
                categoria: 'comercial',
            },
            pdfHash: 'mock_pdf_hash_1',
            blockchainHash: '0x' + Math.random().toString(16).substr(2, 64),
            ipfsHash: 'QmMockHash1' + Math.random().toString(36).substr(2, 20),
            status: 'issued',
            issuedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
            transactionHash: '0x' + Math.random().toString(16).substr(2, 64),
        };
        // Mock document 2: Certificado de Mantenimiento
        const doc2 = {
            id: 'mock_doc_2',
            templateId: 'mock_template_maintenance',
            templateName: 'Certificado de Mantenimiento de Aeronaves',
            issuerId: 'testadmin@test.com',
            issuerName: 'Taller de Reparación Test',
            recipientEmail: testRecipientEmail,
            recipientName: 'Juan Pérez',
            fieldValues: {
                nombre: 'Juan Pérez',
                matricula: 'LV-ABC123',
                tipoMantenimiento: 'Mantenimiento Anual',
                fecha: '2025-01-15',
                horas: '1500',
                observaciones: 'Aeronave en perfecto estado',
            },
            metadata: {
                tipo: 'certificado_mantenimiento',
                categoria: 'anual',
            },
            pdfHash: 'mock_pdf_hash_2',
            blockchainHash: '0x' + Math.random().toString(16).substr(2, 64),
            ipfsHash: 'QmMockHash2' + Math.random().toString(36).substr(2, 20),
            status: 'issued',
            issuedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
            transactionHash: '0x' + Math.random().toString(16).substr(2, 64),
        };
        // Mock document 3: Certificado Genérico
        const doc3 = {
            id: 'mock_doc_3',
            templateId: undefined,
            templateName: 'Documento Personalizado',
            issuerId: 'testadmin@test.com',
            issuerName: 'Institución Test',
            recipientEmail: testRecipientEmail,
            recipientName: 'Juan Pérez',
            fieldValues: {
                curso: 'Curso de Especialización',
                duracion: '40 horas',
                fechaInicio: '2024-11-01',
                fechaFin: '2024-12-15',
            },
            metadata: {
                tipo: 'certificado_curso',
                categoria: 'educacion',
                freeFields: {
                    'Instructor': 'Carlos Rodríguez',
                    'Calificación': 'Aprobado',
                    'Nota Final': '95/100',
                },
            },
            pdfHash: 'mock_pdf_hash_3',
            blockchainHash: '0x' + Math.random().toString(16).substr(2, 64),
            ipfsHash: 'QmMockHash3' + Math.random().toString(36).substr(2, 20),
            status: 'issued',
            issuedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
            transactionHash: '0x' + Math.random().toString(16).substr(2, 64),
        };
        // Save all mock documents
        this.saveDocument(doc1);
        this.saveDocument(doc2);
        this.saveDocument(doc3);
        console.log('Mock documents created for', testRecipientEmail);
    }
}
export const documentService = new DocumentService();
// Initialize mock documents on service load
if (typeof window !== 'undefined') {
    // Only run in browser
    documentService.initializeMockDocuments();
}
